import { Controller, useForm } from "react-hook-form";
import {
  Button,
  ButtonsContainer,
  Container,
  ContainerHeader,
  FormContainer,
  Title,
} from "./style";
import { yupResolver } from "@hookform/resolvers/yup";
import { listarFornecedoresSchema } from "../../validate/validator";
import { Col, Form, Row } from "react-bootstrap";
import { CustomInput } from "components/CustomInput";
import { IFiltro } from "../../interfaces/IFiltro";
import { DropdownSelect, IOptions } from "components/DropdownSelect";
import { useEffect, useMemo, useState } from "react";
import { useMaskInput } from "hooks/useMaskInput";
import { useMunicipios } from "hooks/useMunicipios";
import { Multiselect } from "components/Multiselect";
import { ufs } from "features/core/pages/Veiculo/pages/BloqueioAdministrativo/constants/ufs";

const ATIVIDADES_OCULTAS = ["HONORARIO"];

const isAtividadeOculta = (atividade: string) =>
  ATIVIDADES_OCULTAS.includes(
    atividade
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toUpperCase()
  );

const ordenarOptions = (options: IOptions[]) =>
  [...options].sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));

const optionsUf = ordenarOptions(ufs);

interface IFiltrosProps {
  onSubmit: (dados: IFiltro) => void;
  optionsFornecedor: IOptions[];
  optionsAtividadePrincipal: IOptions[];
  setFiltro: (dados: IFiltro) => void;
  filtro: IFiltro;
}
export function FormFiltrarFornecedor({
  onSubmit,
  optionsFornecedor,
  optionsAtividadePrincipal,
  setFiltro,
  filtro,
}: IFiltrosProps) {
  const { register, handleSubmit, control, setValue } = useForm({
    resolver: yupResolver(listarFornecedoresSchema),
  });

  const [defaultFornecedor, setDefaultFornecedor] = useState<string | null>(
    filtro?.fornecedor ?? null
  );
  const [defaultStatus, setDefaultStatus] = useState<string | null>(
    filtro?.status ?? null
  );
  const [defaultUf, setDefaultUf] = useState<string | null>(
    filtro?.uf ?? null
  );
  const [defaultCidade, setDefaultCidade] = useState<string | null>(
    filtro?.cidade ?? null
  );
  const { municipios, listarMunicipios } = useMunicipios();

  const optionsCidade = useMemo(
    () =>
      ordenarOptions(
        municipios.map((item) => ({ value: item.nome, label: item.nome }))
      ),
    [municipios]
  );

  const optionsAtividadePrincipalAgrupadas = useMemo(() => {
    const descricoes = new Set<string>();

    optionsAtividadePrincipal.forEach(({ label }) => {
      const descricao = String(label).trim();
      if (!isAtividadeOculta(descricao)) {
        descricoes.add(descricao);
      }
    });

    return ordenarOptions(
      Array.from(descricoes, (descricao) => ({
        value: descricao,
        label: descricao,
      }))
    ).map((option) => ({ ...option, checked: false }));
  }, [optionsAtividadePrincipal]);

  useEffect(() => {
    if (filtro?.uf) {
      listarMunicipios(filtro.uf);
    }
  }, []);

  const handleSelecionarUf = (uf: string) => {
    setDefaultUf(uf);
    setDefaultCidade(null);
    setValue("cidade", "");
    listarMunicipios(uf);
  };

  const handleLimpar = () => {
    setDefaultFornecedor(null);
    setDefaultStatus(null);
    setDefaultUf(null);
    setDefaultCidade(null);
    setValue("fornecedor", "");
    setValue("cpfCnpj", "");
    setValue("status", "");
    setValue("uf", "");
    setValue("cidade", "");
    setValue("atividadesPrincipais", []);
    setFiltro({});
  };

  const {
    mInputCPFCNPJ,
  } = useMaskInput();

  return (
    <Container>
      <ContainerHeader>
        <Title>Cadastrar</Title>
      </ContainerHeader>
      <hr />
      <FormContainer>
        <Form
          noValidate
          onSubmit={handleSubmit((dados) => {
            const obj: IFiltro = dados;

            if (defaultFornecedor) {
              obj.fornecedor = defaultFornecedor;
            }

            if (defaultStatus) {
              obj.status = defaultStatus;
            }

            if (defaultUf) {
              obj.uf = defaultUf;
            }

            if (defaultCidade) {
              obj.cidade = defaultCidade;
            }

            const todasAtividadesSelecionadas =
              optionsAtividadePrincipalAgrupadas.length > 0 &&
              obj.atividadesPrincipais?.length ===
                optionsAtividadePrincipalAgrupadas.length;

            if (todasAtividadesSelecionadas) {
              obj.atividadesPrincipais = [];
            }

            onSubmit(obj);
          })}
        >
          <Row className="flex-md-nowrap">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Fornecedor</Form.Label>
                <Controller
                  control={control}
                  name="fornecedor"
                  render={({ field: { onChange, value } }) => (
                    <DropdownSelect
                      options={optionsFornecedor}
                      title=""
                      selectedValue={defaultFornecedor}
                      onSelect={(value) => {
                        onChange(value);
                        setDefaultFornecedor(value);
                      }}
                      searchable
                    />
                  )}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>CPF/CNPJ</Form.Label>
                <CustomInput
                  type="text"
                  {...register("cpfCnpj")}
                  style={{ height: "2.2rem" }}
                  placeholder="Busque por CPF/CNPJ"
                  defaultValue={filtro.cpfCnpj ?? ""}
                  onChange={(element) => {
                    mInputCPFCNPJ(element);
                  }}
                />
              </Form.Group>
            </Col>
            <Col style={{ flex: "0 0 130px" }}>
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Controller
                  control={control}
                  name="status"
                  render={({ field: { onChange, value } }) => (
                    <DropdownSelect
                      options={[
                        { value: "ATIVO", label: "Ativo" },
                        { value: "INATIVO", label: "Inativo" },
                      ]}
                      title=""
                      selectedValue={defaultStatus}
                      onSelect={(value) => {
                        onChange(value);
                        setDefaultStatus(value);
                      }}
                      searchable
                      menuLarguraDoCampo
                      maxLengthPesquisa={7}
                    />
                  )}
                />
              </Form.Group>
            </Col>
            <Col style={{ flex: "0 0 130px" }}>
              <Form.Group>
                <Form.Label>UF</Form.Label>
                <Controller
                  control={control}
                  name="uf"
                  render={({ field: { onChange } }) => (
                    <DropdownSelect
                      options={optionsUf}
                      title=""
                      selectedValue={defaultUf}
                      onSelect={(value) => {
                        onChange(value);
                        handleSelecionarUf(value);
                      }}
                      searchable
                      menuLarguraDoCampo
                      maxLengthPesquisa={2}
                    />
                  )}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Cidade</Form.Label>
                <Controller
                  control={control}
                  name="cidade"
                  render={({ field: { onChange } }) => (
                    <DropdownSelect
                      options={optionsCidade}
                      title=""
                      selectedValue={defaultCidade}
                      onSelect={(value) => {
                        onChange(value);
                        setDefaultCidade(value);
                      }}
                      searchable
                      disabled={!defaultUf}
                    />
                  )}
                />
              </Form.Group>
            </Col>
            <Col style={{ flex: "0 1 300px", minWidth: 0 }}>
              <Form.Group>
                <Form.Label>Atividade Principal</Form.Label>
                <Controller
                  control={control}
                  name="atividadesPrincipais"
                  defaultValue={filtro?.atividadesPrincipais ?? []}
                  render={({ field: { onChange, value } }) => (
                    <Multiselect
                      title=""
                      searchable
                      searchPlaceholder="Pesquisar"
                      selectAll
                      removable
                      options={optionsAtividadePrincipalAgrupadas}
                      onChange={onChange}
                      value={(value ?? []).filter(
                        (v): v is string => !!v
                      )}
                      limitPlaceholder={35}
                    />
                  )}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="justify-content-end" style={{ height: "auto" }}>
            <Col xs="auto">
              <Form.Group>
                <ButtonsContainer style={{ paddingTop: 0 }}>
                  <Button
                    variante="primary"
                    type="button"
                    outline
                    onClick={handleLimpar}
                  >
                    Limpar
                  </Button>
                  <Button type="submit">Buscar</Button>
                </ButtonsContainer>
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </FormContainer>
    </Container>
  );
}
